import { useRef, useEffect, useState, memo } from 'react';
import { Segmented, Typography } from 'antd';
import moment, { Moment } from 'moment';
import ReactECharts from 'echarts-for-react';
import { ProjectDto, ReportDto } from '@/core';
import { DownloadChartBtn } from './DownloadChartBtn';
import { EmptyChart } from './EmptyChart';
import { TotalChartTotalsTypesEnum, getOptionsTotalChart } from './helpers';
import { useGetMaxQuantityShownColumns } from './hooks/useGetMaxQuantityShownColumns';
import classes from './TotalChart.module.scss';

type TotalChartType = {
  projectData: ProjectDto;
  startDate: Moment | null;
  endDate: Moment | null;
};
type useGetTotalChartDataPropsType = TotalChartType & { totalType: string };

const filterReportsByDate = (reports: ReportDto[], startDate: Moment | null, endDate: Moment | null) => {
  return reports.filter((report) => moment(report.metricsDate).isBetween(startDate, endDate, 'month', '[]'));
};

const useGetTotalChartData = ({
  projectData,
  startDate,
  endDate,
  totalType,
}: useGetTotalChartDataPropsType) => {
  const maxShownColumns = useGetMaxQuantityShownColumns();
  const [filteredReportsByDate, setFilteredReportsByDate] = useState(() =>
    filterReportsByDate(projectData.reports, startDate, endDate),
  );

  const isChartEmpty = filteredReportsByDate.filter((report) => report.isDataSource).length;

  useEffect(() => {
    startDate &&
      endDate &&
      setFilteredReportsByDate(filterReportsByDate(projectData.reports, startDate, endDate));
  }, [startDate, endDate, projectData]);

  const { options, isChartExpanded } = getOptionsTotalChart(
    filteredReportsByDate,
    totalType,
    maxShownColumns,
  );

  return { isChartEmpty, options, isChartExpanded };
};

const TotalChart = (props: TotalChartType) => {
  const chartRef = useRef<ReactECharts | null>(null);
  const totalsSelectOptions = Object.values(TotalChartTotalsTypesEnum);
  const [totalType, setTotalType] = useState<string>(TotalChartTotalsTypesEnum.costs);
  const { isChartEmpty, options, isChartExpanded } = useGetTotalChartData({ ...props, totalType });

  const handleChangeTotalType = (totalType: string | number) => {
    setTotalType(String(totalType));
  };

  return (
    <>
      {isChartEmpty ? (
        <div className={classes.totalChartWrapper}>
          <div className={classes.totalChartPanel}>
            <div className={classes.titleWrapper}>
              <Typography.Title level={3} className="mb-0">
                Totals
              </Typography.Title>
              <DownloadChartBtn chartRef={chartRef} fileName="Totals.pdf" />
            </div>
            <div className={classes.totalChartControls}>
              <Segmented onChange={handleChangeTotalType} options={totalsSelectOptions} />
            </div>
          </div>
          <ReactECharts
            ref={chartRef}
            style={{ height: isChartExpanded ? 400 : 345 }}
            notMerge={true}
            option={options}
          />
        </div>
      ) : (
        <EmptyChart title="Totals" />
      )}
    </>
  );
};

export default memo(TotalChart);
